import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map, share } from 'rxjs/operators';
import { PagedResponse } from '../core';
import {
  CreateSalesCreditNoteRequest,
  HostService,
  ListSalesCreditNoteRequest,
  PagedRequestOptions,
  SalesCreditNoteApiService,
} from '@vendasta/billing';
import {
  ListFilters,
  listSalesCreditNoteFiltersToApi,
  SalesCreditNote,
  salesCreditNoteFromApi,
} from './sales-credit-note';

@Injectable({ providedIn: 'root' })
export class SalesCreditNoteService {
  constructor(
    private hostService: HostService,
    private http: HttpClient,
    private salesCreditNoteApi: SalesCreditNoteApiService,
  ) {}

  downloadPdf(salesCreditNoteId: string, merchantId?: string): Observable<Blob> {
    let params;
    if (merchantId) {
      params = { salesCreditNoteId, merchantId };
    } else {
      params = { salesCreditNoteId };
    }

    return this.http
      .get(`${this.hostService.hostWithScheme}/sales-credit-note-pdf`, {
        observe: 'body',
        params: params,
        responseType: 'blob',
        withCredentials: true,
      })
      .pipe(share(), catchError(this.handleDownloadPDFError.bind(this)));
  }

  createAutomatedCreditNote(
    merchantId: string,
    creditNoteReason: string,
    total: number,
    sku: string,
  ): Observable<null> {
    const req = new CreateSalesCreditNoteRequest({
      merchantId,
      creditNoteReason,
      total,
      sku,
    });
    return this.salesCreditNoteApi.create(req).pipe(map(() => null));
  }

  list(filters: ListFilters, cursor: string, pageSize: number): Observable<PagedResponse<SalesCreditNote>> {
    const req = new ListSalesCreditNoteRequest({
      filters: listSalesCreditNoteFiltersToApi(filters),
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.salesCreditNoteApi.list(req).pipe(
      map((response) => {
        const salesCreditNotes = (response.creditNote || []).map((i) => salesCreditNoteFromApi(i));
        return new PagedResponse(salesCreditNotes, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  private handleDownloadPDFError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      console.error(`An error occurred:`, error.error.message);
    } else {
      console.error(`Server returned ${error.message}`);
    }
    return observableThrowError(`Unable to download pdf`);
  }
}
